<script setup>
import { ref } from 'vue';
import shuffle from 'lodash/shuffle.js';
import AppButton from '@/components/AppButton.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';

const props = defineProps({
    students: {
        type: Array,
        required: true,
    },
    startingPoint: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['teamsGenerated']);

const processing = ref(false);
const numberOfTeams = ref(6);

async function generateOnSubmit() {
    processing.value = true;
    console.time('generateTeamsForSize');

    const teams = generateTeamsForSize();
    console.timeEnd('generateTeamsForSize');
    emit('teamsGenerated', teams);

    processing.value = false;
}

const getStudentCompatibility = (student, team) => {
    let compatibility = {
        positive: 0,
        negative: 0,
        positive_inverse: 0,
        negative_inverse: 0,
        total: 0,
        positive_total: 0,
        negative_total: 0,
    };

    team.students.forEach((s) => {
        compatibility.positive += student.positive.includes(s.id);
        compatibility.negative -= student.negative.includes(s.id);
        compatibility.positive_inverse += s.positive.includes(student.id);
        compatibility.negative_inverse -= s.negative.includes(student.id);
    });

    compatibility.positive_total = compatibility.positive + compatibility.positive_inverse;
    compatibility.negative_total = compatibility.negative + compatibility.negative_inverse;
    compatibility.total = compatibility.positive_total + compatibility.negative_total;
    return compatibility;
};

const getTeamCompatibility = (team) => {
    let compatibility = {
        positive: 0,
        negative: 0,
        positive_inverse: 0,
        negative_inverse: 0,
        total: 0,
        positive_total: 0,
        negative_total: 0,
    };

    team.students.forEach((student) => {
        const studentCompatibility = getStudentCompatibility(student, team);

        compatibility.positive += studentCompatibility.positive;
        compatibility.negative += studentCompatibility.negative;
        compatibility.positive_inverse += studentCompatibility.positive_inverse;
        compatibility.negative_inverse += studentCompatibility.negative_inverse;
        compatibility.total += studentCompatibility.total;
        compatibility.positive_total += studentCompatibility.positive_total;
        compatibility.negative_total += studentCompatibility.negative_total;
    });

    return compatibility;
};

const getTeamsCompatibility = (teams) => {
    let compatibility = {
        positive: 0,
        negative: 0,
        positive_inverse: 0,
        negative_inverse: 0,
        total: 0,
        positive_total: 0,
        negative_total: 0,
    };

    teams.forEach((t) => {
        const teamCompatibility = getTeamCompatibility(t);
        compatibility.positive += teamCompatibility.positive;
        compatibility.negative += teamCompatibility.negative;
        compatibility.positive_inverse += teamCompatibility.positive_inverse;
        compatibility.negative_inverse += teamCompatibility.negative_inverse;
        compatibility.total += teamCompatibility.total;
        compatibility.positive_total += teamCompatibility.positive_total;
    });

    return compatibility;
};

const generateTeamsForSize = () => {
    const maxTeams = numberOfTeams.value;
    let studentList = JSON.parse(JSON.stringify(props.students));
    studentList.sort((a, b) => {
        return b.negative_inverse.length - a.negative_inverse.length;
    });
    const initialTeams = props.startingPoint.filter((t) => t.students.length > 0);
    const teams = generateTeams(studentList, maxTeams, initialTeams);

    const scoredOptions = [];
    const compatibility = getTeamsCompatibility(teams);
    let negative = compatibility.negative;
    let positive = -compatibility.positive;
    scoredOptions.push({
        compatibility,
        teams,
    });

    for (let i = 0; i < 2000; i++) {
        const shuffledStudents = shuffle(studentList);
        const teams = generateTeams(shuffledStudents, maxTeams, initialTeams);
        const compatibility = getTeamsCompatibility(teams);

        if (compatibility.negative >= negative && compatibility.positive > positive) {
            negative = compatibility.negative;
            positive = compatibility.positive;
            scoredOptions.push({
                compatibility,
                teams,
            });
        }
    }

    scoredOptions.sort((a, b) => {
        if (a.compatibility.negative === b.compatibility.negative) {
            return b.compatibility.positive - a.compatibility.positive;
        }

        return b.compatibility.negative - a.compatibility.negative;
    });

    const scoredOption = scoredOptions[0];

    return scoredOption.teams;
};

function removeStudent(students, studentId) {
    //find student by id property and remove from the students array, then return student
    const index = students.findIndex((student) => student.id == studentId);

    // If the student is found (index !== -1)
    if (index !== -1) {
        // Remove the student from the array
        return students.splice(index, 1)[0]; // Return the removed student
    } else {
        // Handle case where student is not found (optional)
        return null; // Or throw an error, etc.
    }
}

function setInitialTeams(teams, students, initialTeams) {
    initialTeams.forEach((t) => {
        let team = createNewTeam();
        t.students.forEach((s) => {
            let student = removeStudent(students, s.id);
            if (!student) {
                return;
            }
            team.students.push(student);
        });

        teams.push(team);
    });

    return teams;
}

function createNewTeam() {
    return {
        id: null,
        name: null,
        students: [],
    };
}

const generateTeams = (studentList, maxTeams, initialTeams = []) => {
    let students = JSON.parse(JSON.stringify(studentList));

    const maxPerTeam = Math.floor(students.length / maxTeams);

    let teams = [];
    const secondPass = [];
    const thirdPass = [];
    const isFull = (t) => {
        return t.students.length >= maxPerTeam;
    };
    const isNotFull = (t) => {
        return !isFull(t);
    };
    teams = setInitialTeams(teams, students, initialTeams);

    const positiveCompatibility = (student, team) => {
        const compatibility = getStudentCompatibility(student, team);
        return compatibility.positive_total >= 0 && compatibility.negative_total === 0;
    };
    const sortBySizeDesc = (a, b) => {
        return b.students.length - a.students.length;
    };
    const sortBySizeAsc = (a, b) => {
        return a.students.length - b.students.length;
    };

    const sortByStudentCompatibility = (s, a, b) => {
        const aCompatibility = getStudentCompatibility(s, a);
        const bCompatibility = getStudentCompatibility(s, b);

        if (aCompatibility.total !== bCompatibility.total) {
            return bCompatibility.total - aCompatibility.total;
        }

        if (aCompatibility.negative_total !== bCompatibility.negative_total) {
            return Math.abs(bCompatibility.negative_total) - Math.abs(aCompatibility.negative_total);
        }

        if (aCompatibility.positive_total !== bCompatibility.positive_total) {
            return bCompatibility.positive_total - aCompatibility.positive_total;
        }

        return bCompatibility.total - aCompatibility.total;
    };
    const getTeamForStudent = (student) => {
        const addTeam = () => {
            const t = createNewTeam();
            teams.push(t);
        };
        if (teams.length === 0) {
            addTeam();

            return teams[0];
        }

        const filteredTeams = teams
            .filter(isNotFull)
            .filter((t) => positiveCompatibility(student, t))
            .sort((a, b) => sortByStudentCompatibility(student, b, a))
            .sort(sortBySizeDesc);

        if (filteredTeams.length) {
            return filteredTeams[0];
        }

        if (teams.length < maxTeams) {
            addTeam();

            return teams[teams.length - 1];
        }

        return null;
    };
    const getTeamForStudentSecondPass = (student) => {
        const filteredTeams = teams
            .filter(isNotFull)
            .filter((t) => positiveCompatibility(student, t))
            .sort((a, b) => sortByStudentCompatibility(student, a, b));

        if (filteredTeams.length) {
            return filteredTeams[0];
        }

        return null;
    };
    const getTeamForStudentThirdPass = (student) => {
        const filteredTeams = teams.sort((a, b) => sortByStudentCompatibility(student, a, b)).sort(sortBySizeAsc);
        return filteredTeams[0];
    };

    while (students.length > 0) {
        const student = students.pop();

        const team = getTeamForStudent(student);

        if (team === null) {
            secondPass.push(student);
        } else {
            team.students.push(student);
        }
    }

    while (secondPass.length > 0) {
        const student = secondPass.pop();

        const team = getTeamForStudentSecondPass(student);

        if (team === null) {
            thirdPass.push(student);
        } else {
            team.students.push(student);
        }
    }

    while (thirdPass.length > 0) {
        const student = thirdPass.pop();

        const team = getTeamForStudentThirdPass(student);

        if (team !== null) {
            team.students.push(student);
        }
    }

    return teams;
};
</script>

<template>
    <form action="" @submit.prevent="generateOnSubmit" class="flex gap-2 items-end p-2 bg-gray-200 rounded-2xl mt-1">
        <div>
            <FormLabel :value="$t('messages.number_of_teams')" for="qty" />
            <FormInput
                type="number"
                name="qty"
                id="qty"
                v-model.number="numberOfTeams"
                min="2"
                :max="students.length"
                class="w-20"
            />
        </div>
        <AppButton
            element="button"
            type="submit"
            :disabled="numberOfTeams > students.length || numberOfTeams < 2 || processing"
        >
            {{ $t('messages.generate_teams_automatically') }}
            <span v-if="processing" class="ml-1 btn-spinner" />
        </AppButton>
    </form>
</template>
